// utils/canonicalizeFileStreaming.js
// Streaming / low-memory canonicalizer for large files (CSV + Excel).
// Produces a canonical CSV copy with:
//   - strict header rename (order preserved)
//   - CreatedTime normalization (same logic as canonicalizeFile.js)
//   - Message Type value remap (post-process semantics)
//   - Excel-like string length guard (MAX_SAFE)
// Progress: pass { onProgress } to receive { phase: 'validate'|'write', rows: number }.
// Abort: pass { signal } (AbortController).

const CANON_CREATED = 'CreatedTime';
// const CANON_MESSAGE_TYPE = 'Message Type';
const MAX_SAFE = 32765;
const VALIDATION_LIMIT = 1000; // validate at most 1000 data rows

export async function canonicalizeFileStreaming(
  file,
  mappingSrcToCanon,
  requiredCanon = [],
  valueMaps = {},
  { onProgress, signal } = {}
) {
  const ext = (file.name.split('.').pop() || '').toLowerCase();

  if (ext === 'csv') {
    return canonicalizeCSV_streaming(file, mappingSrcToCanon, requiredCanon, valueMaps, { onProgress, signal });
  }

  if (ext === 'xlsx' || ext === 'xls') {
    return canonicalizeExcel_iterate(file, mappingSrcToCanon, requiredCanon, valueMaps, { onProgress, signal });
  }

  throw new Error(`Unsupported file type for streaming canonicalization: .${ext}`);
}

/* ----------------------------- CSV (Papa) ----------------------------- */

async function canonicalizeCSV_streaming(file, mappingSrcToCanon, requiredCanon, valueMaps, { onProgress, signal }) {
  const Papa = (await import('papaparse')).default || (await import('papaparse'));

  // Peek headers and delimiter
  // const { headers: srcHeaders, delim } = await new Promise((resolve, reject) => {
  const { headers: srcHeaders} = await new Promise((resolve, reject) => {
    Papa.parse(file, {
      preview: 1,
      worker: true,
      skipEmptyLines: 'greedy',
      complete: (res) => {
        try {
          const first = res.data?.[0] || [];
          const headers = first.map(h => (h == null ? '' : String(h)));
          const delimiter = res.meta?.delimiter || detectDelimFromFirst(headers);
          resolve({ headers, delim: delimiter });
        } catch (e) { reject(e); }
      },
      error: reject
    });
  });

  const newHeaders = renameHeaders(srcHeaders, mappingSrcToCanon);
  enforceRequired(newHeaders, requiredCanon);

  // const srcHeaderIdx = buildHeaderIndex(srcHeaders);
  // const tgtToSrcName = newHeaders.map((tgtHeader) => {
  //   const mappedSrc = Object.entries(mappingSrcToCanon).find(([src, canon]) => canon === tgtHeader)?.[0];
  //   const candidateSrc = mappedSrc ?? tgtHeader;
  //   const idx = srcHeaderIdx[normHeader(candidateSrc)];
  //   return idx == null || idx < 0 ? null : srcHeaders[idx];
  // });

  const srcCreated = Object.entries(mappingSrcToCanon).find(([src, canon]) => canon === CANON_CREATED)?.[0] ?? null;
  // const rawMsgMapXLSX = getMsgTypeRawMap(valueMaps);
  // const msgTypeMapNorm = rawMsgMapXLSX ? buildNormMap(rawMsgMapXLSX) : null;

  // Pass A: validate CreatedTime
  await new Promise((resolve, reject) => {
    if (!srcCreated) return resolve(); // nothing to validate
    let rowNum = 2; // header is row 1
    const bad = [];
    let aborted = false;
    Papa.parse(file, {
      header: true,
      worker: true,
      skipEmptyLines: 'greedy',
      step: (results, parser) => {
        if (signal?.aborted) { aborted = true; parser.abort(); return; }
        const row = results.data;
        const shown = row?.[srcCreated] == null ? '' : String(row[srcCreated]);
        const norm = normalizeCreatedTime(shown, null);
        if (norm == null && bad.length < 6) bad.push({ r: rowNum, value: shown });
        rowNum++;
        if (onProgress && rowNum % 10000 === 0) onProgress({ phase: 'validate', rows: rowNum - 2 });
        if (rowNum > VALIDATION_LIMIT + 1) {  parser.abort();
          return;
         }
      },
      complete: () => {
        if (aborted) return reject(new DOMException('Aborted', 'AbortError'));
        if (bad.length) {
          return reject(createdTimeError(bad));
        }
        return resolve();
      },
      error: reject
    });
  });

  // Pass B: write canonical CSV
  // const parts = [];
  // const esc = (x) => csvEscape(x, delim);
  // parts.push(newHeaders.map(v => esc(v)).join(delim) + '\r\n');

  // await new Promise((resolve, reject) => {
  //   let rowNum = 2;
  //   let aborted = false;
  //   Papa.parse(file, {
  //     header: true,
  //     worker: true,
  //     skipEmptyLines: 'greedy',
  //     step: (results, parser) => {
  //       if (signal?.aborted) { aborted = true; parser.abort(); return; }

  //       const row = results.data || {};
  //       const outVals = newHeaders.map((tgtHeader, i) => {
  //         const srcName = tgtToSrcName[i];
  //         let raw = srcName ? row[srcName] : '';

  //         if (tgtHeader === CANON_CREATED) {
  //           const shown = raw == null ? '' : String(raw);
  //           const norm = normalizeCreatedTime(shown, null);
  //           return esc(norm ?? '');
  //         }

  //         if (tgtHeader === CANON_MESSAGE_TYPE && msgTypeMapNorm) {
  //           const shown = raw == null ? '' : String(raw);
  //           const k = normKey(shown);
  //           const mapped = Object.prototype.hasOwnProperty.call(msgTypeMapNorm, k) ? msgTypeMapNorm[k] : shown;
  //           return esc(mapped);
  //         }

  //         return esc(raw);
  //       });

  //       parts.push(outVals.join(delim) + '\r\n');
  //       rowNum++;
  //       if (onProgress && rowNum % 10000 === 0) onProgress({ phase: 'write', rows: rowNum - 2 });
  //     },
  //     complete: () => {
  //       if (aborted) return reject(new DOMException('Aborted', 'AbortError'));
  //       resolve();
  //     },
  //     error: reject
  //   });
  // });

  // const outBlob = new Blob(parts, { type: 'text/csv' });
  // return new File([outBlob], toCsvName(file.name), { type: 'text/csv' });
  return true;
}

/* ----------------------------- Excel (SheetJS) ----------------------------- */

async function canonicalizeExcel_iterate(file, mappingSrcToCanon, requiredCanon, valueMaps, { onProgress, signal }) {
  const XLSX = await import('xlsx');

  const buf = await file.arrayBuffer();
  // NOTE: XLSX.read does parse the workbook; we keep row-level memory small by iterating cells directly.
  const wb = XLSX.read(buf, { type: 'array', raw: true, cellDates: false });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws) throw new Error('No worksheet found');

  // Sprinklr exports place dashboard metadata above the actual header row.
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  let headerRow = range.s.r;
  for (let r = range.s.r; r <= Math.min(range.e.r, range.s.r + 9); r++) {
    const rowValues = [];
    for (let c = range.s.c; c <= range.e.c; c++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      rowValues.push(normHeader(cell?.v));
    }
    if (rowValues.includes('conversation stream') ||
        (rowValues.includes('created time') && rowValues.includes('message type'))) {
      headerRow = r;
      break;
    }
  }

  const srcHeaders = [];
  for (let c = range.s.c; c <= range.e.c; c++) {
    const addr = XLSX.utils.encode_cell({ r: headerRow, c });
    const cell = ws[addr];
    const h = cell?.v == null ? '' : String(cell.v);
    srcHeaders.push(h);
  }

  const newHeaders = renameHeaders(srcHeaders, mappingSrcToCanon);
  enforceRequired(newHeaders, requiredCanon);

  const srcHeaderIdx = buildHeaderIndex(srcHeaders);
  // const tgtToSrcIdx = newHeaders.map((tgtHeader) => {
  //   const mappedSrc = Object.entries(mappingSrcToCanon).find(([src, canon]) => canon === tgtHeader)?.[0];
  //   const candidateSrc = mappedSrc ?? tgtHeader;
  //   const idx = srcHeaderIdx[normHeader(candidateSrc)];
  //   return idx == null ? -1 : idx;
  // });

  // const msgTypeMapNorm = buildNormMap(getMsgTypeRawMap(valueMaps));
  const srcCreatedIdx = (() => {
    const mappedSrc = Object.entries(mappingSrcToCanon).find(([src, canon]) => canon === CANON_CREATED)?.[0];
    if (!mappedSrc) return -1;
    const idx = srcHeaderIdx[normHeader(mappedSrc)];
    return idx == null ? -1 : idx;
  })();

  // Pass A: validate CreatedTime (iterate rows)
  if (srcCreatedIdx !== -1) {
    const bad = [];
    let rowNum = 2; // first data row
    for (let r = headerRow + 1; r <= range.e.r; r++) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      const addrSrc = XLSX.utils.encode_cell({ r, c: srcCreatedIdx });
      const srcCell = ws[addrSrc];
      const shown = readDisplayedStringFromCell(srcCell, XLSX);
      const norm = normalizeCreatedTime(shown, srcCell);
      if (norm == null && bad.length < 6) bad.push({ r: rowNum, value: shown });
      rowNum++;
      if (onProgress && rowNum % 10000 === 0) onProgress({ phase: 'validate', rows: rowNum - 2 });
      if (rowNum > VALIDATION_LIMIT + 1) break;
    }
    if (bad.length) throw createdTimeError(bad);
  }

  // Pass B: write canonical CSV
  // const delim = ','; // output CSV delimiter
  // const parts = [];
  // const esc = (x) => csvEscape(x, delim);

  // parts.push(newHeaders.map(v => esc(v)).join(delim) + '\r\n');

  // let rowNum = 2;
  // for (let r = range.s.r + 1; r <= range.e.r; r++) {
  //   if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  //   const outVals = newHeaders.map((tgtHeader, i) => {
  //     const srcIdx = tgtToSrcIdx[i];
  //     const addr = srcIdx >= 0 ? XLSX.utils.encode_cell({ r, c: srcIdx }) : null;
  //     const srcCell = addr ? ws[addr] : undefined;

  //     if (tgtHeader === CANON_CREATED) {
  //       const shown = readDisplayedStringFromCell(srcCell, XLSX);
  //       const norm = normalizeCreatedTime(shown, srcCell);
  //       return esc(norm ?? '');
  //     }

  //     if (tgtHeader === CANON_MESSAGE_TYPE && msgTypeMapNorm) {
  //       const shown = readDisplayedStringFromCell(srcCell, XLSX);
  //       const k = normKey(shown);
  //       const mapped = Object.prototype.hasOwnProperty.call(msgTypeMapNorm, k) ? msgTypeMapNorm[k] : shown;
  //       return esc(mapped);
  //     }

  //     // Other cells: prefer displayed string; else v->string; trim to Excel-safe
  //     const shown = readDisplayedStringFromCell(srcCell, XLSX);
  //     return esc(shown);
  //   });

  //   parts.push(outVals.join(delim) + '\r\n');
  //   rowNum++;
  //   if (onProgress && rowNum % 10000 === 0) onProgress({ phase: 'write', rows: rowNum - 2 });
  // }

  // const outBlob = new Blob(parts, { type: 'text/csv' });
  // return new File([outBlob], toCsvName(file.name), { type: 'text/csv' });
  return true;
}

/* ----------------------------- Shared helpers ----------------------------- */

function readDisplayedStringFromCell(cell, XLSX) {
  if (!cell) return '';
  if (typeof cell.w === 'string') return safeExcelString(cell.w);
  if (cell.v != null) {
    try {
      const shown = XLSX.SSF?.format ? XLSX.SSF.format(cell.z || 'General', cell.v) : String(cell.v);
      return safeExcelString(String(shown));
    } catch {
      return safeExcelString(String(cell.v));
    }
  }
  return '';
}

function createdTimeError(bad) {
  return new Error(
    'CreatedTime must be convertible to UTC.\n' +
      'Accepted: ISO-like `YYYY-MM-DD[ T]HH:mm:ss[.fff]` (with Z/±offset or none), ' +
      'US `MM/DD/YYYY hh:mm:ss AM/PM`, or Excel date values.\n' +
      'First bad examples:\n' +
      bad.map(b => `row ${b.r}: "${b.value}"`).join('\n')
  );
}

function detectDelimFromFirst(headers) {
  // Cheap fallback: prefer comma unless first line clearly uses tab/semicolon
  const first = headers.join(',');
  const cand = [',', '\t', ';'].map(d => ({
    d,
    count: (first.match(new RegExp(`\\${d}`, 'g')) || []).length
  })).sort((a, b) => b.count - a.count);
  return (cand[0]?.count || 0) > 0 ? cand[0].d : ',';
}

// function csvEscape(x, delim) {
//   const sRaw = x == null ? '' : String(x);
//   const s = typeof sRaw === 'string' ? safeExcelString(sRaw) : sRaw;
//   const mustQuote = /[",\r\n]/.test(s) || s.includes(delim);
//   const y = s.replace(/"/g, '""');
//   return mustQuote ? `"${y}"` : y;
// }

function safeExcelString(val) {
  if (typeof val !== 'string') return val;
  return val.length > MAX_SAFE ? val.slice(0, MAX_SAFE) : val;
}

// function appendSuffix(name, suffix) {
//   const dot = name.lastIndexOf('.');
//   return dot === -1 ? name + suffix : name.slice(0, dot) + suffix + name.slice(dot);
// }

function renameHeaders(headers, mappingSrcToCanon) {
  return headers.map(h => (h in mappingSrcToCanon ? mappingSrcToCanon[h] : h));
}

function enforceRequired(newHeaders, requiredCanon) {
  const have = new Set(newHeaders);
  const missing = (requiredCanon || []).filter(k => !have.has(k));
  if (missing.length) throw new Error(`Missing required mapped field(s): ${missing.join(', ')}`);
}

function normHeader(s) {
  return String(s ?? '').normalize('NFKC').replace(/\s+/g, ' ').trim().toLowerCase();
}
function buildHeaderIndex(headers) {
  const idx = Object.create(null);
  headers.forEach((h, i) => {
    const key = normHeader(h);
    if (!(key in idx)) idx[key] = i; // keep first occurrence if duplicates
  });
  return idx;
}

// function normKey(s) {
//   return String(s ?? '').normalize('NFKC').replace(/\s+/g, ' ').trim().toLowerCase();
// }
// function buildNormMap(rawMap) {
//   // handle null/undefined/non-object defensively
//   if (!rawMap || typeof rawMap !== 'object') return null;

//   const out = Object.create(null);
//   for (const [k, v] of Object.entries(rawMap)) {
//     out[normKey(k)] = v;   // allow mapping to '' if desired
//   }
//   return out;
// }

// function getMsgTypeRawMap(valueMaps) {
//   if (!valueMaps) return null;
//   if (valueMaps[CANON_MESSAGE_TYPE]) return valueMaps[CANON_MESSAGE_TYPE];
//   const k = Object.keys(valueMaps).find(
//     key => normHeader(key) === normHeader(CANON_MESSAGE_TYPE) || /message\s*type/i.test(key)
//   );
//   return k ? valueMaps[k] : null;
// }

/* ------------------------- CreatedTime normalization ------------------------- */
// Output: "YYYY-MM-DD HH:mm:ss[.fff]+00:00"
// Accepts:
//  • ISO-like ±TZ/Z or none (assume UTC)
//  • US "MM/DD/YYYY hh:mm:ss AM/PM"
//  • Excel serial numbers (only meaningful when srcCell provided)

function normalizeCreatedTime(raw, srcCell) {
  if (raw == null) return null;

  // Excel numeric serials (Excel path)
  if (srcCell && srcCell.t === 'n' && typeof srcCell.v === 'number') {
    const d = excelSerialToDateUTC(srcCell.v);
    if (!d) return null;
    return formatUTC(d, msFromSerial(srcCell.v));
  }

  const s = String(raw).trim();
  if (!s) return null;

  // ISO with offset or Z (allow space instead of T)
  if (ISO_OFFSET_RE.test(s)) {
    const d = new Date(s.replace(' ', 'T'));
    if (isFinite(d)) return formatUTC(d);
  }

  // ISO-like w/o tz (assume UTC)
  const n = s.match(ISO_NAIVE_RE);
  if (n) {
    const [, y, mon, day, hour, min, sec, frac] = n;
    const ms = frac ? fracToMs(frac) : 0;
    const d = new Date(Date.UTC(+y, +mon - 1, +day, +hour, +min, +sec, ms));
    return formatUTC(d, frac ? ms : null);
  }

  // US locale "MM/DD/YYYY hh:mm:ss AM/PM"
  const u = s.match(US_AMPM_RE);
  if (u) {
    let [, mm, dd, yyyy, hh, mi, ss, ampm] = u;
    let H = (+hh) % 12;
    if (/pm/i.test(ampm)) H += 12;
    const d = new Date(Date.UTC(+yyyy, +mm - 1, +dd, H, +mi, +ss, 0));
    return formatUTC(d);
  }

  // Last resort
  const d2 = new Date(s);
  return isFinite(d2) ? formatUTC(d2) : null;
}

const ISO_OFFSET_RE =
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(\.\d{1,9})?(Z|[+-]\d{2}:\d{2}|[+-]\d{2}\d{2}|[+-]\d{2})$/;
const ISO_NAIVE_RE =
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(\.\d{1,9})?$/;
const US_AMPM_RE =
  /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM)$/i;

function formatUTC(date, msOverride = null) {
  const pad = (n, w = 2) => String(n).padStart(w, '0');
  const Y = date.getUTCFullYear();
  const Mo = pad(date.getUTCMonth() + 1);
  const D = pad(date.getUTCDate());
  const H = pad(date.getUTCHours());
  const Mi = pad(date.getUTCMinutes());
  const S = pad(date.getUTCSeconds());
  const ms = msOverride != null ? msOverride : date.getUTCMilliseconds();
  const ms3 = String(ms).padStart(3, '0');
  return `${Y}-${Mo}-${D} ${H}:${Mi}:${S}.${ms3}+00:00`;
}

function fracToMs(frac) {
  const digits = String(frac).replace('.', '');
  return Math.round(Number('0.' + digits) * 1000);
}

function excelSerialToDateUTC(serial) {
  if (typeof serial !== 'number' || !isFinite(serial)) return null;
  const ms = Math.round((serial - 25569) * 86400 * 1000); // from 1899-12-30
  return new Date(ms);
}

function msFromSerial(serial) {
  const frac = serial - Math.trunc(serial);
  return Math.round((frac * 86400 * 1000) % 1000);
}

// function toCsvName(name, suffix = '_mapped') {
//   const dot = name.lastIndexOf('.');
//   const base = dot === -1 ? name : name.slice(0, dot);
//   return `${base}${suffix}.csv`;
// }
